import { writeDraftNote } from '@/components/documents/draft-note';

/*
 * ═══════════════════════════════════════════════════════════════════════════
 * A deal's **Next task**, recorded as a note on the deal and on its company.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Owner, 2026-08-31: the deal form's Next task field records its content as a
 * NOTE — one against the deal, one against that deal's company — written again
 * on every change, so successive next tasks build a history.
 *
 * ⚠️ **It lives here rather than in either caller because there are TWO.** It
 * shipped in `DealFormModal` (`f94cdde`) and the inline editor on the deal
 * record now does the same thing, and what would drift between two copies is
 * the BODY that lands on a company's timeline and the rule deciding whether a
 * note is written at all. A reader looking at one screen has no way to see the
 * other disagreeing with it: both report success, and the two timelines simply
 * say different things.
 *
 * ⚠️ **`writeDraftNote` is the wire and stays the wire.** Everything hard about
 * a refused note — the specificity ordering, the `/notes` 403 sentence, the
 * never-throws guarantee — is there, once. This module is the deal-shaped
 * arrangement on top of it and adds no second way to post a note.
 */

/**
 * A **Next task** note that was attempted and refused.
 *
 * ⚠️ Only ATTEMPTED writes appear here. A deal naming no company writes one
 * note, not two, and reports nothing about the second — "nothing was attempted"
 * and "something failed" must never share a list, or somebody goes looking for a
 * company timeline entry that was never meant to exist.
 */
export interface NextTaskNoteFailure {
  /** Which timeline it was meant to land on, as the reader would name it. */
  where: 'this deal' | 'the company on this deal';
  message: string;
}

/** Where the two notes go. Ids, never a record — the caller owns the record. */
export interface NextTaskNoteTarget {
  dealId: number;
  /** The deal's name as the SERVER holds it, for the company note's attribution. */
  dealName: string;
  /** `null` is ordinary: most deals here name no company, and that is not a fault. */
  companyId: number | null;
}

/**
 * What goes on the COMPANY's timeline.
 *
 * ⚠️ The deal's own note is the text ALONE — the reader is already on the deal.
 * The company's is not: a bare sentence appearing under an account with nothing
 * saying where it came from is unattributable, and a company carries deals from
 * several people. One short line, and the deal name is the whole of the
 * attribution — this is note content, not an explanation of the feature.
 */
export function nextTaskCompanyNoteBody(dealName: string, text: string): string {
  return `Next task on ${dealName}: ${text}`;
}

/**
 * The text to record on this save, or `''` for "nothing to record".
 *
 * ⚠️ Compared against the value LOADED FROM THE RECORD, never against "is this
 * field non-empty". Without that, editing a closing date on a deal that already
 * carries a next task would append an identical note every time, and the history
 * the owner asked for becomes a list of duplicates nobody can read. Saving the
 * same text twice writes nothing; on create the stored value is `''`, so any
 * typed text is a change.
 *
 * Trimmed on BOTH sides because the payload sends the trimmed value: a save that
 * only added a trailing space changes nothing on the record and must not claim a
 * new next task either.
 *
 * ⚠️ **Clearing the field records nothing**, and that is deliberate rather than
 * an omission: an empty body is a 422, and "there is no next task now" is not a
 * sentence worth a timeline entry — the column already says it.
 */
export function nextTaskNoteText(typed: string, stored: string): string {
  const next = typed.trim();
  return next !== '' && next !== stored.trim() ? next : '';
}

/**
 * Writes the Next task note against the deal and, when there is one, its
 * company. **Never throws** — `writeDraftNote` swallows and reports, which is
 * what keeps a refused note out of a caller's `catch`, where it would be
 * rendered as a FAILED save of a deal that is sitting in the list.
 *
 * ⚠️ Sequential and independent: the company note is still attempted when the
 * deal note is refused. The two are separate rows on separate timelines and a
 * 403 on one is a 403 on both, so stopping early would report one failure and
 * hide the other.
 *
 * ⚠️ `/notes` is one of the THREE modules the server genuinely enforces by
 * permission set — so this can 403 for somebody who may perfectly well write
 * the deal. The field is deliberately NOT gated on it: gating would hide a
 * `d_deals` column from a person allowed to write it, on the strength of an
 * unrelated module's permission.
 */
export async function writeNextTaskNotes(
  target: NextTaskNoteTarget,
  text: string,
): Promise<NextTaskNoteFailure[]> {
  if (text === '') return [];

  const failures: NextTaskNoteFailure[] = [];

  const onDeal = await writeDraftNote({ kind: 'deal', id: target.dealId }, text);
  if (onDeal?.kind === 'failed') {
    failures.push({ where: 'this deal', message: onDeal.message });
  }

  /*
   * A deal may name no company — the ordinary case on most records here, not a
   * fault. No request is made and nothing is reported: a note that was never
   * attempted is not a note that failed.
   */
  const { companyId } = target;
  if (companyId === null || !Number.isFinite(companyId)) return failures;

  const onCompany = await writeDraftNote(
    { kind: 'company', id: companyId },
    nextTaskCompanyNoteBody(target.dealName, text),
  );
  if (onCompany?.kind === 'failed') {
    failures.push({ where: 'the company on this deal', message: onCompany.message });
  }

  return failures;
}

/**
 * The refusals as ONE sentence, for a surface with a single message slot —
 * the inline editor's status line.
 *
 * ⚠️ **It says what did NOT happen and nothing about what did.** The caller is
 * reporting a save that landed, and the acknowledgement is the reporting
 * surface's own (`InlineField` supplies the `Saved.` lead on a `partial`), so a
 * second one composed here would put two claims about the same write in one
 * line, worded differently.
 *
 * Each failure keeps the server's own sentence verbatim: on a `/notes` 403 that
 * is the only part naming what a reader can do about it.
 *
 * `DealFormModal` renders the same facts as a LIST rather than calling this —
 * it has a whole report block and can afford one line each. The wording of a
 * single failure is identical either way; only the arrangement differs.
 */
export function describeNextTaskNoteFailures(failures: readonly NextTaskNoteFailure[]): string {
  return failures
    .map((failure) => `The note on ${failure.where} was not recorded: ${failure.message}`)
    .join(' ');
}
